import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/api/client";
import { PROJECT_STAGES_BY_KEY } from "@/lib/workflow";
import Logo from "@/components/Logo";
import {
  ChevronLeft, Loader2, AlertTriangle, Camera, Paperclip, MessageSquare,
  ShoppingCart, ClipboardList, Upload, Send, X, CheckCircle2, LogOut,
} from "lucide-react";
import { toast } from "sonner";

const PROD_STATUS_META = {
  in_progress:   { label: "In Progress",       color: "#0369A1", bg: "#dbeafe" },
  info_required: { label: "Information Required", color: "#c2410c", bg: "#ffedd5" },
  on_hold:       { label: "On Hold",           color: "#b91c1c", bg: "#fee2e2" },
  completed:     { label: "Finished",          color: "#059669", bg: "#d1fae5" },
};
const FIELD = "w-full bg-white border border-slate-300 rounded-sm p-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none";

const stripHK = (pid) => (pid || "").replace(/^HK\s*-\s*/i, "");

/**
 * Mobile-optimised project view for Production & Installation crews.
 * Reads /api/project/{id}/snapshot for a redacted (no pricing, no PII) view of
 * the modules + accessories. Also renders:
 *   - Read-only Special Requirements
 *   - Procurement status
 *   - Attachments from Production Coordination (filtered by uploader_role)
 *   - Internal Notes (shared thread; everyone reads & posts)
 *   - Raise Issue with user tagging
 *   - Photo uploads
 *   - Production status update (Production role only)
 */
export default function MobileProjectView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [snap, setSnap] = useState(null);
  const [notes, setNotes] = useState([]);
  const [issues, setIssues] = useState([]);
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("overview");

  const load = async () => {
    try {
      const [s, n, i] = await Promise.all([
        api.get(`/project/${id}/snapshot`),
        api.get(`/project/${id}/notes`),
        api.get(`/project/issues`, { params: { project_id: id } }),
      ]);
      setSnap(s.data); setNotes(n.data); setIssues(i.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/users"); setUsers(data); }
      catch (e) { /* users endpoint may be restricted for shop-floor roles */ }
    })();
  }, []);

  if (err) return <MobileShell><div className="text-red-600 p-4">{err}</div></MobileShell>;
  if (!snap) return <MobileShell><div className="p-4 text-slate-500">Loading…</div></MobileShell>;

  const stageMeta = PROJECT_STAGES_BY_KEY[snap.stage] || {};
  const prodStatus = (snap.production_status || {}).status || "in_progress";
  const prodMeta = PROD_STATUS_META[prodStatus] || PROD_STATUS_META.in_progress;
  const canUpdateProdStatus = ["production", "super_admin"].includes(user?.role);
  const prodCoordAttachments = (snap.attachments || []).filter((a) => a.uploaded_by_role === "production_coordination");

  return (
    <MobileShell user={user} onLogout={async () => { await logout(); navigate("/login"); }}>
      <header className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-10">
        <Link to="/m" className="hk-tiny-label text-orange-600 inline-flex items-center gap-1">
          <ChevronLeft className="w-3 h-3" /> All my projects
        </Link>
        <h1 className="text-lg font-black tracking-tight mt-1" style={{ fontFamily: "Chivo" }}>
          {snap.customer_name} - {snap.name}
        </h1>
        <div className="flex items-center gap-2 flex-wrap mt-1">
          <span className="font-mono text-[10px] font-bold text-slate-600" data-testid="mob-pid">{stripHK(snap.project_id)}</span>
          <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded-sm"
            style={{ color: stageMeta.color, backgroundColor: stageMeta.bg }}>{stageMeta.label}</span>
          <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded-sm" data-testid="mob-prod-status"
            style={{ color: prodMeta.color, backgroundColor: prodMeta.bg }}>
            {prodMeta.label}
            {["info_required", "on_hold"].includes(prodStatus) && <AlertTriangle className="w-2.5 h-2.5 inline ml-0.5" />}
          </span>
        </div>
        {snap.assigned_sales_name && (
          <div className="text-[10px] text-slate-500 mt-1" data-testid="mob-sales-person">
            Sales: <b className="text-slate-700">{snap.assigned_sales_name}</b>
          </div>
        )}
      </header>

      {/* Tab bar */}
      <nav className="bg-white border-b border-slate-200 flex overflow-x-auto no-scrollbar text-xs font-bold uppercase tracking-tight" data-testid="mob-tabs">
        {[
          ["overview", "Overview"],
          ["stations", `Stations (${(snap.production_stations || []).length})`],
          ["modules", "Modules"],
          ["notes", `Notes (${notes.length})`],
          ["issues", `Issues (${issues.length})`],
        ].map(([key, label]) => (
          <button type="button" key={key} onClick={() => setTab(key)} data-testid={`mob-tab-${key}`}
            className={`px-4 py-3 whitespace-nowrap border-b-2 ${tab === key ? "border-orange-500 text-orange-600" : "border-transparent text-slate-600"}`}>
            {label}
          </button>
        ))}
      </nav>

      <main className="p-4 space-y-4 max-w-md mx-auto pb-24">
        {tab === "overview" && (
          <>
            {canUpdateProdStatus && (
              <ProductionStatusCard projectId={snap.id} current={prodStatus} currentReason={snap.production_status?.reason} onSaved={load} />
            )}
            {snap.special_requirements && (
              <div className="hk-card p-4" data-testid="mob-special-req">
                <div className="hk-tiny-label mb-2">Special requirements</div>
                <div className="text-sm bg-amber-50 border border-amber-200 rounded-sm p-2 whitespace-pre-wrap">{snap.special_requirements}</div>
                <div className="text-[10px] text-slate-400 mt-1">Read only</div>
              </div>
            )}
            <div className="hk-card p-4" data-testid="mob-procurement">
              <div className="hk-tiny-label mb-2 flex items-center gap-1"><ShoppingCart className="w-3 h-3" /> Procurement</div>
              <ProcurementBar proc={snap.procurement || {}} />
            </div>
            {prodCoordAttachments.length > 0 && (
              <div className="hk-card p-4" data-testid="mob-attachments">
                <div className="hk-tiny-label mb-2 flex items-center gap-1"><Paperclip className="w-3 h-3" /> From Production Coord</div>
                <ul className="space-y-1.5">
                  {prodCoordAttachments.map((a) => (
                    <li key={a.file_id}>
                      <a href={`${api.defaults.baseURL}/project/${snap.id}/files/${a.file_id}`} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1">
                        <Paperclip className="w-3 h-3" /> File {a.file_id.slice(0, 8)}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <UploadPhotoCard projectId={snap.id} onSaved={load} />
          </>
        )}
        {tab === "stations" && <StationsTab projectId={snap.id} stations={snap.production_stations || []} canUpdate={["production", "installation", "super_admin"].includes(user?.role)} userRole={user?.role} onSaved={load} />}
        {tab === "modules" && <ModulesList lines={snap.lines || []} extras={snap.extra_items || []} />}
        {tab === "notes" && <NotesTab projectId={snap.id} notes={notes} onSaved={load} user={user} />}
        {tab === "issues" && <IssuesTab projectId={snap.id} issues={issues} users={users} onSaved={load} />}
      </main>
    </MobileShell>
  );
}

function MobileShell({ children, user, onLogout }) {
  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col">
      {user && (
        <div className="bg-white border-b border-slate-200 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo className="h-8 w-8" />
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">{user.name}</div>
          </div>
          <button type="button" onClick={onLogout} data-testid="mob-logout" className="text-slate-500 p-1"><LogOut className="w-4 h-4" /></button>
        </div>
      )}
      {children}
    </div>
  );
}

function ProductionStatusCard({ projectId, current, currentReason, onSaved }) {
  const [status, setStatus] = useState(current || "in_progress");
  const [reason, setReason] = useState(currentReason || "");
  const [saving, setSaving] = useState(false);
  const needsReason = ["info_required", "on_hold"].includes(status);
  const changed = status !== current || reason !== (currentReason || "");
  const submit = async () => {
    if (needsReason && !reason.trim()) return;
    setSaving(true);
    try {
      await api.post(`/project/${projectId}/production-status`, { status, reason: reason.trim() });
      toast.success("Status updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="hk-card p-4" data-testid="prod-status-card">
      <div className="hk-tiny-label mb-2">My production status</div>
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(PROD_STATUS_META).map(([key, meta]) => (
          <button type="button" key={key} onClick={() => setStatus(key)} data-testid={`prod-status-${key}`}
            className={`text-xs font-black uppercase px-2 py-2 rounded-sm border-2 transition-colors ${status === key ? "" : "border-transparent"}`}
            style={{ color: meta.color, backgroundColor: meta.bg, borderColor: status === key ? meta.color : "transparent" }}>
            {meta.label}
          </button>
        ))}
      </div>
      {needsReason && (
        <textarea className={`${FIELD} mt-2`} rows={2} placeholder="Reason (required)"
          value={reason} onChange={(e) => setReason(e.target.value)} data-testid="prod-status-reason" />
      )}
      <button type="button" disabled={!changed || saving || (needsReason && !reason.trim())} onClick={submit} data-testid="prod-status-submit"
        className="mt-2 w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold px-4 py-2 rounded-sm text-sm">
        {saving ? <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> : null} Update status
      </button>
      <p className="text-[10px] text-slate-500 mt-2">Visible to everyone on the project — including dealers.</p>
    </div>
  );
}

function ProcurementBar({ proc }) {
  const status = proc.status || "not_started";
  const meta = {
    not_started: { label: "Not started", color: "#0f172a", bg: "#e2e8f0" },
    in_process:  { label: "In process",  color: "#c2410c", bg: "#ffedd5" },
    received:    { label: "Received",    color: "#059669", bg: "#d1fae5" },
  }[status];
  return (
    <div>
      <div className="inline-block text-[10px] font-black uppercase px-2 py-1 rounded-sm"
        style={{ color: meta.color, backgroundColor: meta.bg }}>{meta.label}</div>
      {proc.notes && <div className="text-xs text-slate-500 mt-1">{proc.notes}</div>}
    </div>
  );
}

const STATION_STATUS_META = {
  pending:       { label: "Pending",              color: "#0f172a", bg: "#e2e8f0" },
  in_progress:   { label: "In Progress",          color: "#0369A1", bg: "#dbeafe" },
  info_required: { label: "Information Required", color: "#c2410c", bg: "#ffedd5" },
  on_hold:       { label: "On Hold",              color: "#b91c1c", bg: "#fee2e2" },
  completed:     { label: "Completed",            color: "#059669", bg: "#d1fae5" },
};

function StationsTab({ projectId, stations, canUpdate, userRole, onSaved }) {
  const ordered = [...stations].sort((a, b) => (a.order || 0) - (b.order || 0));
  // iter 89 — Production users see only production-owned stations;
  // Installation users see only installation-owned. Super Admin still sees
  // everything so they can audit.
  const scoped = ordered.filter((s) => {
    if (userRole === "super_admin") return true;
    const owner = s.owner_role || "production";
    if (userRole === "production")   return owner === "production";
    if (userRole === "installation") return owner === "installation";
    return true;
  });
  if (scoped.length === 0) {
    return <div className="hk-card p-4 text-xs text-slate-500" data-testid="mob-stations-empty">
      {ordered.length === 0
        ? "No production stations configured yet."
        : `Nothing to do — every station on this project is owned by another crew.`}
    </div>;
  }
  return (
    <div className="space-y-2" data-testid="mob-stations-list">
      <p className="text-[11px] text-slate-500 px-1">Update each station&apos;s status. Blocked statuses require a reason.</p>
      {scoped.map((st) => (
        <StationRow key={st.key} projectId={projectId} station={st} canUpdate={canUpdate}
          onSaved={onSaved} isDispatch={isDispatchStation(ordered, st)} />
      ))}
    </div>
  );
}

// Stations ordered after "Finishing" close the project instead of holding it.
function isDispatchStation(allStations, s) {
  const gate = (allStations || []).find(
    (x) => (x.key || "").toLowerCase() === "finishing" || (x.name || "").trim().toLowerCase() === "finishing");
  if (!gate) return false;
  return (s.order || 0) > (gate.order || 0);
}

function StationRow({ projectId, station, canUpdate, onSaved, isDispatch = false }) {
  const [expanded, setExpanded] = useState(false);
  const [reason, setReason] = useState(station.hold_reason || station.info_reason || "");
  const [saving, setSaving] = useState(false);
  const currentStatus = station.status || "pending";
  const meta = STATION_STATUS_META[currentStatus] || STATION_STATUS_META.pending;
  const dispatch = async () => {
    if (!window.confirm("Are you sure this project is completed?\n\nDispatching marks every remaining station complete and moves the project to Finished Projects.")) return;
    setSaving(true);
    try {
      await api.post(`/project/${projectId}/production/station/${station.key}/dispatch`);
      toast.success("Dispatched — project moved to Finished Projects");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const submit = async (nextStatus) => {
    if (["on_hold", "info_required"].includes(nextStatus) && !reason.trim()) {
      setExpanded(true);
      toast.error("Reason required for this status");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/project/${projectId}/production/station/${station.key}`, {
        status: nextStatus,
        reason: ["on_hold", "info_required"].includes(nextStatus) ? reason.trim() : undefined,
      });
      toast.success(`${station.name}: ${STATION_STATUS_META[nextStatus].label}`);
      setExpanded(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="hk-card p-3" data-testid={`mob-station-${station.key}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-bold text-sm">{station.name}</div>
          {station.updated_by && (
            <div className="text-[10px] text-slate-500">Last: {station.updated_by} · {station.updated_at ? new Date(station.updated_at).toLocaleString() : ""}</div>
          )}
          {(station.hold_reason || station.info_reason) && (
            <div className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded-sm p-1 mt-1">
              <b>Reason:</b> {station.hold_reason || station.info_reason}
            </div>
          )}
        </div>
        <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded-sm shrink-0" data-testid={`mob-station-status-${station.key}`}
          style={{ color: meta.color, backgroundColor: meta.bg }}>
          {meta.label}
        </span>
      </div>
      {canUpdate ? (
        <>
          <div className="grid grid-cols-2 gap-1.5">
            {["in_progress", "completed", "info_required", ...(isDispatch ? [] : ["on_hold"])].map((s) => {
              const m = STATION_STATUS_META[s];
              const isCur = currentStatus === s;
              return (
                <button type="button" key={s} disabled={saving} data-testid={`mob-station-${station.key}-${s}`}
                  onClick={() => {
                    if (["on_hold", "info_required"].includes(s) && !reason.trim()) {
                      setExpanded(true); return;
                    }
                    submit(s);
                  }}
                  className={`text-[11px] font-black uppercase px-2 py-1.5 rounded-sm border-2 ${isCur ? "" : "border-transparent"}`}
                  style={{ color: m.color, backgroundColor: m.bg, borderColor: isCur ? m.color : "transparent" }}>
                  {m.label}
                </button>
              );
            })}
            {isDispatch && (
              <button type="button" disabled={saving} onClick={dispatch}
                data-testid={`mob-station-${station.key}-dispatch`}
                className="text-[11px] font-black uppercase px-2 py-1.5 rounded-sm border-2 border-slate-900 bg-slate-900 text-white">
                Dispatched
              </button>
            )}
          </div>
          {expanded && (
            <textarea className={`${FIELD} mt-2`} rows={2} placeholder="Reason (required for Hold / Info Required)"
              value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`mob-station-${station.key}-reason`} />
          )}
        </>
      ) : (
        <p className="text-[10px] text-slate-500 italic">View only.</p>
      )}
    </div>
  );
}


function ModulesList({ lines, extras }) {
  if (lines.length === 0 && extras.length === 0) {
    return <div className="hk-card p-4 text-xs text-slate-500" data-testid="mob-modules-list">No modules on this project yet.</div>;
  }
  return (
    <div className="space-y-2" data-testid="mob-modules-list">
      {lines.map((ln, i) => (
        <div key={i} className="hk-card p-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-[10px] font-bold text-slate-500">#{ln.line_no || i + 1}</div>
              <div className="font-bold text-sm">{ln.description || ln.item || "Item"}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {[ln.width && `W ${ln.width}`, ln.depth && `D ${ln.depth}`, ln.height && `H ${ln.height}`].filter(Boolean).join(" · ")}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] font-bold text-slate-500">QTY</div>
              <div className="font-black text-lg" style={{ fontFamily: "Chivo" }}>{ln.qty || ln.quantity || 1}</div>
            </div>
          </div>
          {ln.finish && <div className="text-[11px] text-slate-600 mt-1">Finish: {ln.finish}</div>}
          {ln.handle_name && <div className="text-[11px] text-slate-600">Handle: {ln.handle_name}</div>}
        </div>
      ))}
      {extras.length > 0 && (
        <>
          <div className="hk-tiny-label pt-2">Extra items</div>
          {extras.map((ln, i) => (
            <div key={`e${i}`} className="hk-card p-3">
              <div className="font-bold text-sm">{ln.description || ln.item}</div>
              <div className="text-[11px] text-slate-500">Qty: {ln.qty || ln.quantity || 1}</div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function NotesTab({ projectId, notes, onSaved, user }) {
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api.post(`/project/${projectId}/notes`, { text: text.trim() });
      setText("");
      toast.success("Note added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPosting(false); }
  };
  return (
    <div data-testid="mob-notes-tab">
      <div className="hk-card p-3 mb-3">
        <textarea className={FIELD} rows={3} placeholder="Add a note visible to everyone on this project…"
          value={text} onChange={(e) => setText(e.target.value)} data-testid="mob-note-input" />
        <button type="button" onClick={submit} disabled={!text.trim() || posting} data-testid="mob-note-submit"
          className="mt-2 w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold px-3 py-2 rounded-sm text-sm inline-flex items-center justify-center gap-1">
          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post note
        </button>
      </div>
      {notes.length === 0 ? (
        <div className="text-xs text-slate-500 text-center py-6" data-testid="mob-notes-empty">No notes yet.</div>
      ) : (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li key={n.id} className={`hk-card p-3 ${n.author_id === user?.id ? "border-l-2 border-l-orange-500" : ""}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[11px] font-bold text-slate-700">{n.author_name} · <span className="text-slate-400">{(n.author_role || "").replace(/_/g, " ")}</span></div>
                <div className="text-[10px] text-slate-400">{new Date(n.created_at).toLocaleString()}</div>
              </div>
              <div className="text-sm whitespace-pre-wrap">{n.text}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function IssuesTab({ projectId, issues, users, onSaved }) {
  const [creating, setCreating] = useState(false);
  return (
    <div data-testid="mob-issues-tab">
      {!creating ? (
        <button type="button" onClick={() => setCreating(true)} data-testid="mob-raise-issue"
          className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold px-3 py-2 rounded-sm text-sm mb-3 inline-flex items-center justify-center gap-1">
          <AlertTriangle className="w-4 h-4" /> Raise a new issue
        </button>
      ) : (
        <RaiseIssueForm projectId={projectId} users={users} onDone={() => { setCreating(false); onSaved(); }} onCancel={() => setCreating(false)} />
      )}
      {issues.length === 0 ? (
        <div className="text-xs text-slate-500 text-center py-6" data-testid="mob-issues-empty">No issues raised yet.</div>
      ) : (
        <ul className="space-y-2">
          {issues.map((iss) => (
            <li key={iss.id} className={`hk-card p-3 border-l-2 ${iss.status === "resolved" ? "border-l-emerald-500" : "border-l-red-500"}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded-sm bg-slate-100">{iss.status}</div>
                <div className="text-[10px] text-slate-400">{new Date(iss.raised_at).toLocaleString()}</div>
              </div>
              <div className="text-sm">{iss.description}</div>
              <div className="text-[11px] text-slate-500 mt-1">
                By {iss.raised_by_name}
                {iss.tagged_user_ids?.length ? ` · Tagged: ${iss.tagged_user_ids.length}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RaiseIssueForm({ projectId, users, onDone, onCancel }) {
  const [desc, setDesc] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!desc.trim()) return;
    setBusy(true);
    try {
      await api.post("/project/issues", {
        project_id: projectId, description: desc.trim(), tagged_user_ids: selected,
      });
      toast.success("Issue raised");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const toggle = (uid) => setSelected((cur) => cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]);
  return (
    <div className="hk-card p-3 mb-3" data-testid="raise-issue-form">
      <div className="hk-tiny-label mb-2">Raise issue</div>
      <textarea className={FIELD} rows={3} placeholder="Describe the issue…"
        value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="issue-desc" />
      <div className="mt-2">
        <div className="hk-tiny-label mb-1">Tag users ({selected.length})</div>
        <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-sm">
          {users.length === 0 ? (
            <div className="text-xs text-slate-400 p-2">No users available to tag</div>
          ) : users.map((u) => (
            <label key={u.id} className="flex items-center gap-2 p-2 border-b border-slate-100 text-xs cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`tag-${u.id}`} />
              <span className="font-bold">{u.name}</span>
              <span className="text-slate-400 ml-auto">{(u.role || "").replace(/_/g, " ")}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={onCancel} className="flex-1 border border-slate-300 hover:bg-slate-50 font-bold px-3 py-2 rounded-sm text-sm">Cancel</button>
        <button type="button" onClick={submit} disabled={!desc.trim() || busy} data-testid="issue-submit"
          className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold px-3 py-2 rounded-sm text-sm">
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null} Raise
        </button>
      </div>
    </div>
  );
}

const PHOTO_EXTS = [".jpg", ".jpeg", ".png"];

function UploadPhotoCard({ projectId, onSaved }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const upload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    // Client-side filter — only JPG/PNG per user choice (5a)
    const files = Array.from(fileList);
    const bad = files.filter((f) => {
      const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
      return !PHOTO_EXTS.includes(ext) || f.size > 15 * 1024 * 1024;
    });
    if (bad.length > 0) {
      toast.error(`Rejected: ${bad.map((f) => f.name).join(", ")}. Only JPG/PNG up to 15 MB allowed.`);
      return;
    }
    setBusy(true);
    try {
      const category = user?.role === "installation" ? "installation_photos" : "production_photos";
      for (const f of files) {
        const form = new FormData(); form.append("file", f);
        await api.post(`/project/${projectId}/files`, form, {
          params: { category },
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`Uploaded ${files.length} photo${files.length === 1 ? "" : "s"}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="hk-card p-4" data-testid="mob-photo-upload">
      <div className="hk-tiny-label mb-2 flex items-center gap-1"><Camera className="w-3 h-3" /> Upload photos</div>
      <label className="w-full flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-sm p-4 cursor-pointer hover:border-orange-500">
        <Upload className="w-6 h-6 text-slate-400 mb-1" />
        <span className="text-xs text-slate-500">{busy ? "Uploading…" : "Tap to pick or take photos"}</span>
        <span className="text-[10px] text-slate-400">JPG or PNG · up to 15 MB each</span>
        <input type="file" hidden multiple accept=".jpg,.jpeg,.png,image/jpeg,image/png"
          capture="environment" onChange={(e) => upload(e.target.files)} data-testid="mob-photo-input" />
      </label>
    </div>
  );
}
